/**
 * CommunityPulseBar — always-visible banner below Navbar showing live
 * community momentum metrics and feature-unlock progress.
 *
 * - Rotating slideshow of 4 metrics at a time (cycles every 4 seconds)
 * - "Unlock progress" chip on the right showing next AI milestone
 * - Click opens a compact popover with all metrics + all 3 unlock bars
 * - Dismissible per-session (sessionStorage) so it doesn't nag
 * - Data cached server-side 10 min; fetched on mount
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useParams, matchPath } from 'react-router-dom';
import {
  Users, UserPlus, MessageSquare, Calendar, Building2, Handshake, Microscope, Lightbulb,
  Sparkles, Trophy, Lock, X, ChevronUp, ChevronDown,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'aadc_pulse_bar_dismissed_v1';
const ROTATE_MS = 4000;
const SHOWN_AT_A_TIME = 4;

const METRIC_CONFIG = [
  { key: 'members',             label: 'Members',            icon: Users,          color: 'text-amber-600' },
  { key: 'new_members_week',    label: 'New this week',      icon: UserPlus,       color: 'text-emerald-600' },
  { key: 'posts_week',          label: 'Posts this week',    icon: MessageSquare,  color: 'text-blue-600' },
  { key: 'events_month',        label: 'Events this month',  icon: Calendar,       color: 'text-purple-600' },
  { key: 'clubs_active',        label: 'Active clubs',       icon: Building2,      color: 'text-orange-600' },
  { key: 'partners',            label: 'Partners',           icon: Handshake,      color: 'text-rose-600' },
  { key: 'research_articles',   label: 'Research articles',  icon: Microscope,     color: 'text-indigo-600' },
  { key: 'suggestions_pending', label: 'Pending suggestions',icon: Lightbulb,      color: 'text-yellow-600' },
];

function MetricChip({ metric, value, customLabel }) {
  const Icon = metric.icon;
  const label = customLabel || metric.label;
  return (
    <div className="flex items-center gap-1.5 whitespace-nowrap" data-testid={`pulse-metric-${metric.key}`}>
      <Icon className={`w-3.5 h-3.5 ${metric.color}`} />
      <span className="text-xs sm:text-sm font-medium tabular-nums">{value?.toLocaleString?.() ?? value}</span>
      <span className="text-xs text-muted-foreground hidden sm:inline">{label}</span>
    </div>
  );
}

function NextUnlockChip({ unlock }) {
  if (!unlock) return null;
  const pct = Math.min(100, Math.round((unlock.current / unlock.threshold) * 100));
  return (
    <Link
      to="/community-pulse"
      className="flex items-center gap-2 text-xs group"
      data-testid="pulse-next-unlock-chip"
      title={`${unlock.label}: ${unlock.current}/${unlock.threshold}`}
    >
      {unlock.enabled ? (
        <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
      ) : unlock.unlocked ? (
        <Trophy className="w-3.5 h-3.5 text-amber-500" />
      ) : (
        <Lock className="w-3.5 h-3.5 text-muted-foreground" />
      )}
      <span className="hidden md:inline text-muted-foreground group-hover:text-foreground transition-colors">
        {unlock.label}
      </span>
      <div className="h-1.5 w-14 sm:w-20 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full transition-all ${unlock.enabled ? 'bg-emerald-500' : unlock.unlocked ? 'bg-amber-500' : 'bg-primary/60'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="tabular-nums text-muted-foreground hidden sm:inline">
        {unlock.current}/{unlock.threshold}
      </span>
    </Link>
  );
}

export default function CommunityPulseBar() {
  const location = useLocation();
  const [data, setData] = useState(null);
  const [clubData, setClubData] = useState(null);
  const [error, setError] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [rotation, setRotation] = useState(0);

  // Detect if we're on a club-scoped page (/club/:slug, /club/:slug/*)
  const clubSlug = useMemo(() => {
    const m = matchPath('/club/:slug/*', location.pathname) || matchPath('/club/:slug', location.pathname);
    return m?.params?.slug || null;
  }, [location.pathname]);

  useEffect(() => {
    // Session-only dismissal
    setDismissed(sessionStorage.getItem(DISMISS_KEY) === '1');
    let cancelled = false;
    // Always fetch global pulse for unlock progress + fallback metrics
    axios
      .get(`${API}/api/community/pulse`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // When on a club page, fetch club-scoped pulse
  useEffect(() => {
    if (!clubSlug) {
      setClubData(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/api/community/clubs/${clubSlug}/pulse`)
      .then((res) => {
        if (!cancelled) setClubData(res.data);
      })
      .catch(() => {
        if (!cancelled) setClubData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [clubSlug]);

  // Rotate metric slideshow
  useEffect(() => {
    if (!data || dismissed) return;
    const id = setInterval(() => {
      setRotation((r) => r + 1);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [data, dismissed]);

  // Pick visible metrics: club-scoped when available, else global
  const { visibleMetrics, allMetricsForPanel, metricValues, customLabels, isClubScoped } = useMemo(() => {
    if (clubData && clubData.enabled !== false && clubData.order?.length > 0) {
      // Use club-scoped shaped data
      const values = clubData.metrics || {};
      const labels = clubData.labels || {};
      const order = clubData.order || [];
      const withValues = order
        .map((k) => METRIC_CONFIG.find((m) => m.key === k))
        .filter((m) => m && typeof values[m.key] === 'number');
      const start = withValues.length ? (rotation * SHOWN_AT_A_TIME) % withValues.length : 0;
      const visible = [];
      for (let i = 0; i < Math.min(SHOWN_AT_A_TIME, withValues.length); i += 1) {
        visible.push(withValues[(start + i) % withValues.length]);
      }
      return {
        visibleMetrics: visible,
        allMetricsForPanel: withValues,
        metricValues: values,
        customLabels: labels,
        isClubScoped: true,
      };
    }
    // Global
    if (!data) return { visibleMetrics: [], allMetricsForPanel: [], metricValues: {}, customLabels: {}, isClubScoped: false };
    const values = data.metrics || {};
    const withValues = METRIC_CONFIG.filter((m) => typeof values[m.key] === 'number' && values[m.key] > 0);
    if (withValues.length === 0) return { visibleMetrics: [], allMetricsForPanel: [], metricValues: values, customLabels: {}, isClubScoped: false };
    const start = (rotation * SHOWN_AT_A_TIME) % withValues.length;
    const visible = [];
    for (let i = 0; i < Math.min(SHOWN_AT_A_TIME, withValues.length); i += 1) {
      visible.push(withValues[(start + i) % withValues.length]);
    }
    return {
      visibleMetrics: visible,
      allMetricsForPanel: METRIC_CONFIG.filter((m) => typeof values[m.key] === 'number'),
      metricValues: values,
      customLabels: {},
      isClubScoped: false,
    };
  }, [data, clubData, rotation]);

  const nextUnlock = useMemo(() => {
    if (!data?.unlocks) return null;
    // First not-yet-unlocked, else first not-yet-enabled, else first
    return (
      data.unlocks.find((u) => !u.unlocked) ||
      data.unlocks.find((u) => u.unlocked && !u.enabled) ||
      data.unlocks[0]
    );
  }, [data]);

  if (error || dismissed || !data) return null;
  // Hide entirely when membership is too small — showing "4 members" to new
  // visitors is counter-productive. Auto-appears once we have a credible base.
  const memberCount = data?.metrics?.members ?? 0;
  if (!isClubScoped && memberCount < 25) return null;
  // If club admin explicitly disabled club-scoped pulse on their page, hide entirely on that club page
  if (isClubScoped && clubData?.enabled === false) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      className="w-full border-b border-border bg-gradient-to-r from-amber-50/70 via-background to-amber-50/70 dark:from-amber-950/30 dark:to-amber-950/30"
      data-testid="community-pulse-bar"
      data-scope={isClubScoped ? 'club' : 'global'}
    >
      <div className="container mx-auto px-3 py-1.5 flex items-center gap-3">
        <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0 hidden sm:block" />
        {isClubScoped && clubData?.club_name && (
          <span className="text-[10.5px] uppercase tracking-wide text-amber-700 dark:text-amber-300 font-semibold shrink-0 hidden md:inline">
            {clubData.club_name}
          </span>
        )}
        <div className="flex-1 min-w-0 flex items-center gap-3 sm:gap-5 overflow-hidden">
          {visibleMetrics.map((m) => (
            <MetricChip
              key={m.key}
              metric={m}
              value={metricValues[m.key]}
              customLabel={customLabels[m.key]}
            />
          ))}
        </div>
        <NextUnlockChip unlock={nextUnlock} />
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="p-1 rounded hover:bg-muted text-muted-foreground shrink-0"
          data-testid="pulse-toggle-expand"
          aria-label={expanded ? 'Collapse pulse details' : 'Expand pulse details'}
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="p-1 rounded hover:bg-muted text-muted-foreground shrink-0"
          data-testid="pulse-dismiss"
          aria-label="Dismiss community pulse bar"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="border-t border-border bg-background/60 backdrop-blur-sm" data-testid="pulse-expanded-panel">
          <div className="container mx-auto px-3 py-3 grid gap-3 md:grid-cols-2">
            {/* All metrics */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                {isClubScoped ? `${clubData?.club_name || 'Club'} Activity` : 'Community Activity'}
              </h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {allMetricsForPanel.map((m) => {
                  const Icon = m.icon;
                  const label = customLabels[m.key] || m.label;
                  return (
                    <div key={m.key} className="flex items-center gap-2 text-sm">
                      <Icon className={`w-4 h-4 ${m.color}`} />
                      <span className="tabular-nums font-medium">{metricValues[m.key]?.toLocaleString?.() ?? 0}</span>
                      <span className="text-xs text-muted-foreground">{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Unlock roadmap */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Feature Unlock Roadmap
              </h3>
              <div className="space-y-2">
                {data.unlocks?.map((u) => {
                  const pct = Math.min(100, Math.round((u.current / u.threshold) * 100));
                  const Icon = u.enabled ? Sparkles : u.unlocked ? Trophy : Lock;
                  const color = u.enabled ? 'bg-emerald-500' : u.unlocked ? 'bg-amber-500' : 'bg-primary/60';
                  const iconColor = u.enabled ? 'text-emerald-500' : u.unlocked ? 'text-amber-500' : 'text-muted-foreground';
                  return (
                    <div key={u.id} data-testid={`pulse-unlock-${u.id}`}>
                      <div className="flex items-center gap-2 text-xs mb-1">
                        <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
                        <span className="font-medium">{u.label}</span>
                        <span className="text-muted-foreground ml-auto tabular-nums">
                          {u.current.toLocaleString()}/{u.threshold.toLocaleString()}
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                        <div className={`h-full transition-all ${color}`} style={{ width: `${pct}%` }} />
                      </div>
                      {u.description && (
                        <p className="text-[10.5px] text-muted-foreground mt-1">{u.description}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
